import type { ReactElement } from 'react';
import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  MoreVertical,
  Loader,
  UserCheck,
  UserX,
  X,
  RefreshCw,
  Shield,
  AlertCircle,
} from 'lucide-react';
import { usersService } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import UserModal from './modals/UserModal';
import type { AdminUser, CreateUserDto, UpdateUserDto } from '../../types/api';

const mapBackendUserToFrontend = (backendUser: any): AdminUser => {
  return {
    ...backendUser,
    status: backendUser.active ? 'active' : 'inactive',
  };
};

const mapUsersArrayToFrontend = (backendUsers: any[]): AdminUser[] => {
  return backendUsers.map(mapBackendUserToFrontend);
};
const UsersManagement = (): ReactElement => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const { addToast, toasts, removeToast } = useToast();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? 'null') as {
        role?: string;
      } | null;
    } catch {
      return null;
    }
  }, []);

  const canManageUsers = ['SYSTEM_ADMIN', 'ADMIN'].includes(user?.role ?? '');

  const availableRoles = useMemo(() => {
    if (user?.role === 'SYSTEM_ADMIN') {
      return [
        { value: 'SYSTEM_ADMIN', label: 'Administrador del Sistema' },
        { value: 'ADMIN', label: 'Administrador' },
        { value: 'GENERAL_MANAGER', label: 'Gerente General' },
        { value: 'ZONE_MANAGER', label: 'Gerente de Zona' },
        { value: 'REPRESENTATIVE', label: 'Representante' },
        { value: 'SELLER', label: 'Vendedora' },
        { value: 'MKT', label: 'Marketing' },
      ];
    }

    if (user?.role === 'ADMIN') {
      // ADMIN NO puede crear SYSTEM_ADMIN
      return [
        { value: 'ADMIN', label: 'Administrador' },
        { value: 'GENERAL_MANAGER', label: 'Gerente General' },
        { value: 'ZONE_MANAGER', label: 'Gerente de Zona' },
        { value: 'REPRESENTATIVE', label: 'Representante' },
        { value: 'SELLER', label: 'Vendedora' },
        { value: 'MKT', label: 'Marketing' },
      ];
    }

    return [];
  }, [user?.role]);

  const hasActiveFilters = useMemo(() => {
    return !!(searchTerm || selectedRole || selectedStatus);
  }, [searchTerm, selectedRole, selectedStatus]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const currentMenuRef = menuRefs.current.get(openMenuId);
        if (currentMenuRef && !currentMenuRef.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openMenuId) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openMenuId]);

  const loadUsers = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await usersService.getAll({
        limit: 1000,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      // Mapear datos del backend al formato del frontend
      const allUsers = mapUsersArrayToFrontend(response.data);
      const filteredUsers = allUsers.filter((user) => {
        if ((user as any).seller) {
          return false;
        }
        return true;
      });

      setUsers(filteredUsers);
    } catch (err) {
      addToast('error', 'Error al cargar la lista de usuarios');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar usuarios
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.employeeNumber.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = !selectedRole || user.role === selectedRole;
      const matchesStatus = !selectedStatus || user.status === selectedStatus;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, selectedRole, selectedStatus]);

  const getRoleLabel = (role: string): string => {
    const roleLabels = {
      SYSTEM_ADMIN: 'Administrador del Sistema',
      ADMIN: 'Administrador',
      GENERAL_MANAGER: 'Gerente General',
      ZONE_MANAGER: 'Gerente de Zona',
      REPRESENTATIVE: 'Representante',
      SELLER: 'Vendedora',
      MKT: 'Marketing',
    };
    return roleLabels[role as keyof typeof roleLabels] || role;
  };

  const getRoleBadgeColor = (role: string): string => {
    const colors = {
      SYSTEM_ADMIN: 'bg-red-100 text-red-800',
      ADMIN: 'bg-orange-100 text-orange-800',
      GENERAL_MANAGER: 'bg-purple-100 text-purple-800',
      ZONE_MANAGER: 'bg-blue-100 text-blue-800',
      REPRESENTATIVE: 'bg-green-100 text-green-800',
      SELLER: 'bg-gray-100 text-gray-800',
      MKT: 'bg-teal-100 text-teal-800',
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadgeColor = (status: string): string => {
    return status === 'active'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const handleCreateUser = (): void => {
    setEditingUser(null);
    setShowCreateModal(true);
    setOpenMenuId(null);
  };

  const handleEditUser = (user: AdminUser): void => {
    setEditingUser(user);
    setShowCreateModal(true);
    setOpenMenuId(null);
  };

  const handleSaveUser = async (
    userData: CreateUserDto | UpdateUserDto
  ): Promise<void> => {
    try {
      let backendUser: any;

      if (editingUser) {
        backendUser = await usersService.update(
          editingUser.id,
          userData as UpdateUserDto
        );
        addToast(
          'success',
          `Usuario ${userData.name} actualizado correctamente`
        );
      } else {
        backendUser = await usersService.create(userData as CreateUserDto);
        addToast('success', `Usuario ${userData.name} creado correctamente`);
      }

      // Mapear y actualizar usuario específico sin recargar toda la lista
      const mappedUser = mapBackendUserToFrontend(backendUser);

      if (editingUser) {
        setUsers((prevUsers) =>
          prevUsers.map((u) => (u.id === editingUser.id ? mappedUser : u))
        );
      } else {
        setUsers((prevUsers) => [...prevUsers, mappedUser]);
      }

      setShowCreateModal(false);
      setEditingUser(null);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Error al guardar usuario';
      addToast('error', message);
      throw err;
    }
  };

  const handleDeleteUser = async (user: AdminUser): Promise<void> => {
    if (
      !confirm(
        `¿Estás seguro de que quieres eliminar permanentemente el usuario "${user.name}"?`
      )
    ) {
      return;
    }

    try {
      await usersService.delete(user.id);
      addToast('error', `Usuario ${user.name} eliminado correctamente`);
      await loadUsers();
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Error al eliminar usuario';
      addToast('error', message);
    }
    setOpenMenuId(null);
  };

  const toggleUserStatus = async (user: AdminUser): Promise<void> => {
    try {
      let updatedUser: AdminUser;

      if (user.status === 'active') {
        const backendUser = await usersService.deactivate(user.id);
        updatedUser = mapBackendUserToFrontend(backendUser);
        addToast('success', `Usuario ${user.name} desactivado`);
      } else {
        const backendUser = await usersService.activate(user.id);
        updatedUser = mapBackendUserToFrontend(backendUser);
        addToast('success', `Usuario ${user.name} activado`);
      }

      // Actualizar estado local inmediatamente
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? updatedUser : u))
      );
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Error al cambiar estado del usuario';
      addToast('error', message);
    }
    setOpenMenuId(null);
  };

  const toggleMenu = (userId: string, event?: React.MouseEvent): void => {
    event?.preventDefault();
    event?.stopPropagation();

    setOpenMenuId(openMenuId === userId ? null : userId);
  };

  const setMenuRef = (userId: string, element: HTMLDivElement | null): void => {
    if (element) {
      menuRefs.current.set(userId, element);
    } else {
      menuRefs.current.delete(userId);
    }
  };

  if (!canManageUsers) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">
              No tienes permisos para gestionar usuarios
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col sm:flex-row items-center justify-center h-64 gap-2 px-4">
        <Loader className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-primary-dark" />
        <span className="text-sm sm:text-base text-gray-600 text-center">
          Cargando usuarios...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mx-4 sm:mx-0">
        <div className="text-red-800 text-sm sm:text-base">
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={loadUsers}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header con acciones */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Gestión de Usuarios
          </h2>
          <p className="text-sm text-gray-600">
            {filteredUsers.length} de {users.length} usuarios
          </p>
        </div>
        <button
          onClick={handleCreateUser}
          className="flex items-center gap-2 bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nuevo Usuario
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full"
              />
            </div>
          </div>

          {/* Rol */}
          <div className="relative w-full md:w-56">
            <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todos los roles</option>
              <option value="SYSTEM_ADMIN">Administrador del Sistema</option>
              <option value="ADMIN">Administrador</option>
              <option value="GENERAL_MANAGER">Gerente General</option>
              <option value="ZONE_MANAGER">Gerente de Zona</option>
              <option value="REPRESENTATIVE">Representante</option>
              <option value="SELLER">Vendedora</option>
            </select>
          </div>

          {/* Estado */}
          <div className="relative w-full md:w-48">
            <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>

          {/* Botón Refresh */}
          <button
            onClick={() => void loadUsers()}
            disabled={loading}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Active Filters Badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-600">Filtros activos:</span>

            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-green-800 text-xs rounded-full">
                Búsqueda: "{searchTerm}"
                <button
                  onClick={() => {
                    setSearchTerm('');
                  }}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedRole && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                Rol: {getRoleLabel(selectedRole)}
                <button
                  onClick={() => {
                    setSelectedRole('');
                  }}
                  className="hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedStatus && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Estado: {selectedStatus === 'active' ? 'Activos' : 'Inactivos'}
                <button
                  onClick={() => {
                    setSelectedStatus('');
                  }}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedRole('');
                setSelectedStatus('');
              }}
              className="text-xs text-gray-600 hover:text-gray-900 underline ml-2"
            >
              Limpiar todos los filtros
            </button>
          </div>
        )}
      </div>

      {/* Tabla de usuarios */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Usuario
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  # Empleado
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Rol
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Estado
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Último Acceso
                </th>
                <th className="text-center py-3 px-4 font-medium text-gray-700">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    <div>
                      <div className="text-xl mb-2">👥</div>
                      <div>No se encontraron usuarios</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div>
                        <div className="text-left font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-left  text-sm text-gray-600">
                          {user.email ?? 'Sin email'}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      <span className="font-mono text-sm">
                        {user.employeeNumber}
                      </span>
                    </td>
                    <td className="text-left  py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(
                          user.role
                        )}`}
                      >
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => void toggleUserStatus(user)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors hover:opacity-80 ${getStatusBadgeColor(
                          user.status
                        )}`}
                      >
                        {user.status === 'active' ? (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Activo
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3 mr-1" />
                            Inactivo
                          </>
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'Nunca'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            handleEditUser(user);
                          }}
                          className="p-1  rounded transition-colors"
                          title="Editar usuario"
                        >
                          <Edit className="w-4 h-4 text-green-500" />
                        </button>

                        {/* Botón de toggle status directo */}
                        <button
                          onClick={() => void toggleUserStatus(user)}
                          className={`p-1 rounded transition-colors ${
                            user.status === 'active'
                              ? 'text-red-600 hover:bg-red-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={
                            user.status === 'active' ? 'Desactivar' : 'Activar'
                          }
                        >
                          {user.status === 'active' ? (
                            <UserX className="w-4 h-4" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                        </button>

                        <div className="relative">
                          <button
                            onClick={(e) => {
                              toggleMenu(user.id, e);
                            }}
                            className="p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-green-950"
                            title="Más opciones"
                            aria-expanded={openMenuId === user.id}
                            aria-haspopup="true"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {openMenuId === user.id && (
                            <div
                              ref={(el) => {
                                setMenuRef(user.id, el);
                              }}
                              className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1"
                              style={{ zIndex: 1000 }}
                            >
                              {/* Editar usuario */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditUser(user);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Editar usuario
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void toggleUserStatus(user);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              >
                                {user.status === 'active' ? (
                                  <>
                                    <UserX className="w-4 h-4 mr-2" />
                                    Desactivar usuario
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Activar usuario
                                  </>
                                )}
                              </button>

                              {/* Divisor */}
                              <div className="border-t border-gray-100 my-1"></div>

                              {/* Eliminar usuario */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleDeleteUser(user);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar usuario
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de crear/editar usuario */}
      <UserModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingUser(null);
        }}
        onSave={handleSaveUser}
        user={editingUser}
        availableRoles={availableRoles}
        currentUserRole={user?.role}
        title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
      />
    </div>
  );
};

export default UsersManagement;
